import { useGLTF } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import { ROOT_MEDIA } from 'shared-base';
import * as THREE from 'three';
import HolographicMaterial from './MaterialParams';

export const Model = ({ id, controlsRef }: any) => {
  const { scene } = useGLTF(`${ROOT_MEDIA}/glbs/${id}.glb`);
  const { camera } = useThree();
  const groupRef = useRef<THREE.Group>(null);

  const holographicMaterial = new HolographicMaterial({
    fresnelOpacity: 1.0,
    fresnelAmount: 0.45,
    scanlineSize: 8.0,
    hologramBrightness: 1.0,
    signalSpeed: 1.0,
    hologramColor: '#00d5ff',
    enableBlinking: true,
    blinkFresnelOnly: true,
    hologramOpacity: 1.0,
  });

  scene.traverse((child) => {
    if ((child as THREE.Mesh).isMesh) {
      (child as THREE.Mesh).material = holographicMaterial;
    }
  });

  useFrame(() => {
    holographicMaterial.update();
  });

  useEffect(() => {
    if (groupRef.current) {
      const box = new THREE.Box3().setFromObject(groupRef.current);
      const size = new THREE.Vector3();
      box.getSize(size);

      const center = new THREE.Vector3();
      box.getCenter(center);

      // Place the model centered at the world origin
      groupRef.current.position.sub(center);

      // Set camera distance based on model size
      const maxDim = Math.max(size.x, size.y, size.z);
      const distance = maxDim * 2; // zoom factor

      // 45° side and 45° top-down angle (isometric style)
      const angle = Math.PI / 4 + Math.PI; // 45 degrees
      const yAngle = Math.atan(1); // 45° + 180° = 225°

      const camX = distance * Math.cos(angle);
      const camZ = distance * Math.sin(angle);
      const camY = distance * Math.sin(yAngle);

      camera.position.set(camX, camY, camZ);
      camera.lookAt(0, 0, 0);
      camera.updateProjectionMatrix();

      if (controlsRef?.current) {
        controlsRef.current.target.set(0, 0, 0);
        controlsRef.current.update();
      }
    }
  }, [scene, camera, controlsRef]);

  return (
    <group ref={groupRef}>
      <primitive object={scene} />
    </group>
  );
};
